import React from 'react'
import { useSelector } from 'react-redux'
import { styled, createTheme } from './stitches.config'
import { RootState } from 'config/user/store'
import { createHSLVariants } from 'utils/function'

const StyledThemeProvider = styled('div', {})

type Props = {
  children: React.ReactNode
}

const getPrimaryDynamicColor = (
  variantColor: string | undefined,
  defaultColor: string
) => {
  const origin = sessionStorage.getItem('origin')
  if (origin === 'fused' && !variantColor) {
    return '#000000' // Fallback for primary colors
  }
  return variantColor || defaultColor
}

const getBackgroundDynamicColor = (
  variantColor: string | undefined,
  defaultColor: string
) => {
  const origin = sessionStorage.getItem('origin')
  if (origin === 'fused' && !variantColor) {
    return '#FFFFFF' // Fallback for background colors
  }
  return variantColor || defaultColor
}

const DynamicThemeProvider: React.FC<Props> = ({ children }) => {
  const themeSettings = useSelector(
    (state: RootState) => state.themeSetting.theme
  )

  const primaryVariants = createHSLVariants(themeSettings.for_color)
  const backgroundVariants = createHSLVariants(themeSettings.back_color)

  const dynamicTheme = React.useMemo(
    () =>
      createTheme({
        colors: {
          primary25: getPrimaryDynamicColor(primaryVariants?.[11], '#E6F6FF'),
          primary50: getPrimaryDynamicColor(primaryVariants?.[10], '#D4EFFE'),
          primary100: getPrimaryDynamicColor(primaryVariants?.[9], '#AAE1FF'),
          primary200: getPrimaryDynamicColor(primaryVariants?.[8], '#80D2FF'),
          primary300: getPrimaryDynamicColor(primaryVariants?.[7], '#55C2FF'),
          primary400: getPrimaryDynamicColor(primaryVariants?.[6], '#2AB3FF'),
          primary500: getPrimaryDynamicColor(primaryVariants?.[5], '#1CAEFF'),
          primary600: getPrimaryDynamicColor(primaryVariants?.[4], '#01A4FE'),
          primary700: getPrimaryDynamicColor(primaryVariants?.[3], '#0074DD'),
          primary800: getPrimaryDynamicColor(primaryVariants?.[2], '#005280'),
          primary900: getPrimaryDynamicColor(primaryVariants?.[1], '#003755'),
          primary950: getPrimaryDynamicColor(primaryVariants?.[0], '#001B2A'),
          gray300: getBackgroundDynamicColor(
            backgroundVariants?.[7],
            '#D0D5DD'
          ),

          // Update these with dynamic values
          primaryBase: getPrimaryDynamicColor(primaryVariants?.[5], '#1CAEFF'),
          primaryBgSubtle: getPrimaryDynamicColor(
            primaryVariants?.[1],
            '#003755'
          ),
          primaryLine: getPrimaryDynamicColor(primaryVariants?.[5], '#1CAEFF'),

          primaryBg: getPrimaryDynamicColor(primaryVariants?.[5], '#1CAEFF'),
          primaryBgLight: getPrimaryDynamicColor(
            primaryVariants?.[10],
            '#D4EFFE'
          ),
          primaryBgHover: getPrimaryDynamicColor(
            primaryVariants?.[4],
            '#01A4FE'
          ),
          primaryBgActive: getPrimaryDynamicColor(
            primaryVariants?.[5],
            '#1CAEFF'
          ),

          primaryBorder: getPrimaryDynamicColor(
            primaryVariants?.[8],
            '#80D2FF'
          ),
          primaryBorderFocus: getPrimaryDynamicColor(
            primaryVariants?.[5],
            '#1CAEFF'
          ),
          primaryBorderHover: getPrimaryDynamicColor(
            primaryVariants?.[7],
            '#55C2FF'
          ),

          primaryDisabled: getPrimaryDynamicColor(
            primaryVariants?.[8],
            '#80D2FF'
          ),

          primarySolid: getPrimaryDynamicColor(primaryVariants?.[5], '#1CAEFF'),
          primarySolidHover: getPrimaryDynamicColor(
            primaryVariants?.[3],
            '#0074DD'
          ),
          primaryText: getPrimaryDynamicColor(primaryVariants?.[3], '#0074DD'),

          // Update other dynamic values
          inputBorder: getBackgroundDynamicColor(
            backgroundVariants?.[7],
            '#D0D5DD'
          ),
        },
      }),
    // Add an inline comment to explain the necessity of these dependencies
    [primaryVariants, backgroundVariants]
  )

  return (
    <StyledThemeProvider className={dynamicTheme.className}>
      {children}
    </StyledThemeProvider>
  )
}

export default DynamicThemeProvider
